import { liveQuery } from "dexie";
import type { HostCapabilities } from "../capabilities";
import type { ShellStore } from "../contract";
import type { MarlinStoreApi } from "./api";
import { initializeLegacy } from "./adoption/api";
import { createCloudApi } from "./cloud/api";
import { createFavouriteSitesApi } from "./favouriteSites/api";
import { createAuditApi } from "./infra/audit/api";
import { DEFAULT_DB_NAME, onVersionChange, openStoreDb } from "./infra/db";
import { createNotificationsApi } from "./notifications/api";
import { createOffsetsApi } from "./offsets/api";
import { createQuickActionsApi } from "./quickActions/api";
import { createResetApi } from "./reset/api";
import { createScreenTemplatesApi } from "./screenTemplates/api";
import { createScreensApi } from "./screens/api";
import { createSelectionApi } from "./selection/api";
import { createSettingsApi } from "./settings/api";
import { createSitesApi } from "./sites/api";
import { createTemplatesApi } from "./templates/api";

export type CreateStoreOptions = { name?: string; onClosed?: () => void };

export const createStore = async (
  capabilities: HostCapabilities,
  options: CreateStoreOptions = {},
) => {
  const db = openStoreDb(options.name ?? DEFAULT_DB_NAME);
  onVersionChange(db, options.onClosed ?? (() => undefined));
  await db.open();
  const screenId = capabilities.screens.current();
  const adoption = await initializeLegacy(db, capabilities, screenId);
  const api: MarlinStoreApi = {
    screenId,
    observe: (query) => liveQuery(query),
    screens: createScreensApi(db, screenId, capabilities),
    sites: createSitesApi(db, capabilities),
    settings: createSettingsApi(db, capabilities),
    selection: createSelectionApi(db, screenId, capabilities),
    templates: createTemplatesApi(db),
    screenTemplates: createScreenTemplatesApi(db, screenId),
    notifications: createNotificationsApi(db, capabilities, screenId),
    quickActions: createQuickActionsApi(db),
    offsets: createOffsetsApi(db),
    favouriteSites: createFavouriteSitesApi(db),
  } satisfies ShellStore;
  return {
    ...api,
    api,
    capabilities,
    audit: createAuditApi(db),
    adoption,
    cloud: createCloudApi(db, capabilities),
    resetStore: createResetApi(db, capabilities, screenId),
  };
};

export type MarlinStore = Awaited<ReturnType<typeof createStore>>;
